import { EventEmitter } from "eventemitter3";
import type { Vector2 } from "~/state/game-types";

type EventTypes = {
  pointerMove: [Vector2, MouseEvent];
  wasdInput: [Vector2];
  pointerPrimaryDown: [MouseEvent];
  pointerSecondaryDown: [MouseEvent];
  pointerPrimaryUp: [MouseEvent];
  pointerSecondaryUp: [MouseEvent];
  escapeKeyDown: [KeyboardEvent];
  inventoryKeyDown: [KeyboardEvent];
  loadoutKeyDown: [number, KeyboardEvent];
};

type KeyCode =
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F"
  | "G"
  | "H"
  | "I"
  | "J"
  | "K"
  | "L"
  | "M"
  | "N"
  | "O"
  | "P"
  | "Q"
  | "R"
  | "S"
  | "T"
  | "U"
  | "V"
  | "W"
  | "X"
  | "Y"
  | "Z"
  | `Arrow${"Up" | "Down" | "Left" | "Right"}`
  | "-"
  | "="
  | "Escape";

/**
 * InputManager is responsible for detecting user inputs and translating them
 * to game actions without containing any game logic.
 */
export class InputManager extends EventEmitter<EventTypes> {
  public keyStates: { [key in KeyCode]?: boolean } = {};

  public wasdDirection: Vector2 = { x: 0, y: 0 };

  public pointerScreenPosition: Vector2 = { x: 0, y: 0 };
  public pointerPrimaryPressed: boolean = false;
  public pointerSecondaryPressed: boolean = false;

  /**
   * Start listening for user input
   */
  public constructor() {
    super();
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.handleBlur);
    window.addEventListener("mousemove", this.handleMouseMove);
    window.addEventListener("mousedown", this.handleMouseDown);
    window.addEventListener("mouseup", this.handleMouseUp);
  }

  /**
   * Stop listening for user input
   */
  public dispose(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.handleBlur);
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mousedown", this.handleMouseDown);
    window.removeEventListener("mouseup", this.handleMouseUp);

    this.keyStates = {};
  }

  /**
   * Handle key down events
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    const code = event.key.length === 1 ? event.key.toUpperCase() : event.key;
    this.keyStates[code as KeyCode] = true;

    // Handle escape key press
    if (code === "Escape") {
      this.emit("escapeKeyDown", event);
    } else if (code === "E") {
      this.emit("inventoryKeyDown", event);
    } else {
      const loadoutNumber = parseInt(code);
      if (!isNaN(loadoutNumber)) {
        this.emit("loadoutKeyDown", loadoutNumber, event);
      }
    }

    this.updateWasdInput();
  };

  /**
   * Handle key up events
   */
  private handleKeyUp = (event: KeyboardEvent): void => {
    const code = event.key.length === 1 ? event.key.toUpperCase() : event.key;
    this.keyStates[code as KeyCode] = false;
    this.updateWasdInput();
  };

  /**
   * Handle window blur (stop all input when window loses focus)
   */
  private handleBlur = (): void => {
    // Clear all key states when window loses focus
    this.keyStates = {};
    this.updateWasdInput();
  };

  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.pointerPrimaryPressed = true;
      this.emit("pointerPrimaryDown", event);
    } else if (event.button === 2) {
      this.pointerSecondaryPressed = true;
      this.emit("pointerSecondaryDown", event);
    }
  };

  private handleMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.pointerPrimaryPressed = false;
      this.emit("pointerPrimaryUp", event);
    } else if (event.button === 2) {
      this.pointerSecondaryPressed = false;
      this.emit("pointerSecondaryUp", event);
    }
  };

  private handleMouseMove = (event: MouseEvent): void => {
    this.pointerScreenPosition.x = event.clientX;
    this.pointerScreenPosition.y = event.clientY;

    this.emit("pointerMove", this.pointerScreenPosition, event);
  };

  /**
   * Translate current key states to movement direction
   */
  private updateWasdInput(): void {
    this.wasdDirection.x = 0;
    this.wasdDirection.y = 0;

    // Detect movement keys (WASD and arrow keys)
    if (this.keyStates["W"] || this.keyStates["ArrowUp"]) {
      this.wasdDirection.y += 1;
    }
    if (this.keyStates["S"] || this.keyStates["ArrowDown"]) {
      this.wasdDirection.y -= 1;
    }
    if (this.keyStates["A"] || this.keyStates["ArrowLeft"]) {
      this.wasdDirection.x -= 1;
    }
    if (this.keyStates["D"] || this.keyStates["ArrowRight"]) {
      this.wasdDirection.x += 1;
    }

    // Send the input to the handler
    this.emit("wasdInput", this.wasdDirection);
  }
}
